import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { API, axios } from '../../utils/api';
import Icona from '../cliente/Icone';
import { errore, quandoBreve } from './comuni';

// Impostazioni → Testi (backend/testi_modificabili.py): i testi lunghi che legge il cliente, corretti da qui senza
// toccare il codice. Oggi sono quelli della pagina «Rinnovo del permesso» (/rinnovo-permesso).
//
// - le voci sono divise in gruppi (Generale e un gruppo per ogni tipo di permesso): si apre un gruppo alla volta,
//   altrimenti sono quaranta campi in fila;
// - le voci «a righe» sono elenchi: UNA RIGA = UN PUNTO della lista che vede il cliente;
// - «Torna com'era» rimette il testo scritto nel codice (in archivio restano solo le voci davvero cambiate);
// - dove nel testo c'è scritto «verificare» manca un numero che nessuno di noi ha verificato: mettetecelo voi.

const CERCA_VERIFICARE = /verificare/gi;
// Nell'avviso «verificare sul sito della Questura» non manca nessun numero: è l'avviso, non si conta
const SENZA_CONTO = ['permesso.avviso'];
// Sotto questa lunghezza basta una riga sola (la data dell'aggiornamento, il nome del tipo)
const CORTO = 80;

export const quanteDaVerificare = (voci) => (voci || []).reduce(
  (somma, v) => somma + (SENZA_CONTO.includes(v.chiave) ? 0 : ((v.testo || '').match(CERCA_VERIFICARE) || []).length), 0);

const Testi = () => {
  const [dati, setDati] = useState(null);
  const [form, setForm] = useState({});
  const [gruppo, setGruppo] = useState(null);
  const [salvo, setSalvo] = useState(false);

  const leggi = (risposta) => {
    setDati(risposta);
    setForm(Object.fromEntries(risposta.voci.map((v) => [v.chiave, v.testo])));
  };

  useEffect(() => {
    axios.get(`${API}/testi`).then((r) => leggi(r.data)).catch(() => setDati({ errore: true }));
  }, []);

  const gruppi = useMemo(() => [...new Set((dati?.voci || []).map((v) => v.gruppo))], [dati]);
  const aperto = gruppo && gruppi.includes(gruppo) ? gruppo : gruppi[0];
  const cambiate = useMemo(
    () => (dati?.voci || []).filter((v) => (form[v.chiave] ?? '') !== v.testo).map((v) => v.chiave), [dati, form]);

  const salva = async () => {
    setSalvo(true);
    try {
      const r = await axios.put(`${API}/testi`, { voci: Object.fromEntries(cambiate.map((c) => [c, form[c]])) });
      leggi(r.data);
      toast.success(cambiate.length === 1 ? 'Testo salvato' : `${cambiate.length} testi salvati`);
    } catch (e) { toast.error(errore(e, 'Non salvati')); } finally { setSalvo(false); }
  };

  if (!dati) return <p className="vuoto">Carico i testi…</p>;
  if (dati.errore) return <p className="vuoto">Non riesco a leggere i testi.</p>;

  const voci = dati.voci.filter((v) => v.gruppo === aperto);
  const daVerificare = quanteDaVerificare(dati.voci);

  return (
    <div className="imp-griglia" data-testid="testi">
      <section className="card">
        <div className="ttl"><div className="i"><Icona nome="documento" /></div><h2>Testi della pagina «Rinnovo del permesso»</h2></div>
        <p className="imp-nota">
          Questi testi li legge il cliente in <b>/rinnovo-permesso</b>, e li leggete anche voi dalla scheda del cliente.
          Una riga = un punto dell&apos;elenco. Il campo svuotato torna al testo scritto nel codice.
        </p>
        <p className="imp-nota" data-testid="testi-verificare">
          {daVerificare > 0
            ? `Dove c'è scritto «verificare» manca un numero che non abbiamo controllato (importi, tempi, quante foto): ancora ${daVerificare} da completare.`
            : 'Non c\'è più nessun «verificare» in sospeso.'}
        </p>
        <div className="imp-azioni" role="group" aria-label="Gruppi di testi">
          {gruppi.map((g) => (
            <button type="button" key={g} className={`chip${g === aperto ? ' on' : ''}`} aria-pressed={g === aperto}
              data-testid={`gruppo-${g}`} onClick={() => setGruppo(g)}>{g}</button>
          ))}
        </div>
        <div className="imp-azioni">
          <button type="button" className="btn sm p" disabled={!cambiate.length || salvo} onClick={salva}>
            {salvo ? 'Salvo…' : 'Salva'}
          </button>
          <span className="imp-nota">
            {cambiate.length ? `${cambiate.length} da salvare` : 'Niente da salvare'}
            {dati.salvato_il ? ` · ultima correzione ${quandoBreve(dati.salvato_il, true)}${dati.salvato_da ? ` da ${dati.salvato_da}` : ''}` : ''}
          </span>
        </div>
      </section>

      <section className="card" data-testid={`testi-gruppo-${aperto}`}>
        <div className="ttl"><div className="i b"><Icona nome="impostazioni" /></div><h2>{aperto}</h2></div>
        <div className="imp-campi">
          {voci.map((v) => (
            <label key={v.chiave} className="largo">
              {v.etichetta}{v.righe ? ' (una riga = un punto)' : ''}
              {v.righe || (v.di_base || '').length > CORTO
                ? (
                  <textarea rows={Math.min(14, Math.max(3, (form[v.chiave] || '').split('\n').length + 2))} value={form[v.chiave] ?? ''}
                    data-testid={`campo-${v.chiave}`} onChange={(e) => setForm({ ...form, [v.chiave]: e.target.value })} />
                )
                : (
                  <input value={form[v.chiave] ?? ''} data-testid={`campo-${v.chiave}`}
                    onChange={(e) => setForm({ ...form, [v.chiave]: e.target.value })} />
                )}
              <span className="imp-nota" style={{ textTransform: 'none', letterSpacing: 0 }}>
                {v.modificato ? 'corretto da voi' : 'testo di partenza'}
                {v.modificato && (
                  <>
                    {' · '}
                    <button type="button" className="btn xs rosso-vuoto" data-testid={`ripristina-${v.chiave}`}
                      onClick={() => setForm({ ...form, [v.chiave]: '' })}>Torna com&apos;era</button>
                  </>
                )}
              </span>
            </label>
          ))}
        </div>
      </section>
    </div>
  );
};

export default Testi;
